// ============================================================================
// MÓDULO UTILITÁRIO: DATAJUD PARSER (INTEGRAÇÃO CNJ LOCAL)
// ARQUIVO: src/utils/DataJudParser.js
// OBJETIVO: Normalizar dados sujos da API DataJud para o padrão RDA.
// ============================================================================

/**
 * Mapeamento de códigos de movimento (CNJ) para status amigáveis
 */
const MOVEMENT_MAP = {
    '22': { label: 'Baixa Definitiva', type: 'ARQUIVADO', color: 'gray' },
    '246': { label: 'Arquivamento Definitivo', type: 'ARQUIVADO', color: 'gray' },
    '196': { label: 'Audiência Designada', type: 'AUDIENCIA', color: 'purple' },
    '193': { label: 'Julgamento', type: 'SENTENCA', color: 'blue' },
    '879': { label: 'Sentença', type: 'SENTENCA', color: 'blue' },
    '11383': { label: 'Ato Ordinatório', type: 'ANDAMENTO', color: 'gray' },
    '60': { label: 'Expedição de Documento', type: 'ANDAMENTO', color: 'green' },
    '85': { label: 'Petição Juntada', type: 'ANDAMENTO', color: 'blue' }
};

/**
 * Parser Principal - Transforma o JSON complexo do CNJ em Objeto Simples
 * @param {Object} rawData - JSON bruto da API DataJud
 */
export const parseDataJudResponse = (rawData) => {
    if (!rawData) return null;

    // 1. Extração de Dados Básicos
    const basicInfo = {
        numeroCNJ: rawData.numeroProcesso || '',
        classe: rawData.classe?.nome || 'Não Classificado',
        assunto: rawData.assuntos?.[0]?.nome || 'Sem Assunto',
        tribunal: rawData.tribunal || 'TJPR',
        dataAjuizamento: rawData.dataAjuizamento ? new Date(rawData.dataAjuizamento).toLocaleDateString('pt-BR') : '-',
        valorCausa: rawData.valorCausa || 0
    };

    // 2. Normalização de Movimentações (Timeline)
    // O CNJ retorna uma árvore, nós achatamos para uma lista cronológica
    const rawMoves = rawData.movimentos || [];
    const timeline = rawMoves.map(mov => {
        const codigo = mov.codigo?.toString();
        const mapped = MOVEMENT_MAP[codigo] || { label: mov.nome || 'Andamento', type: 'GENERIC', color: 'gray' };
        
        return {
            date: mov.dataHora ? new Date(mov.dataHora).toLocaleDateString('pt-BR') : '',
            title: mapped.label,
            description: mov.complemento || mapped.label, // Simplificação
            type: mapped.type,
            color: mapped.color,
            rawCode: codigo
        };
    }).sort((a, b) => {
        // Ordena por data decrescente (mais recente primeiro)
        const dateA = a.date.split('/').reverse().join('-');
        const dateB = b.date.split('/').reverse().join('-');
        return new Date(dateB) - new Date(dateA);
    });

    // 3. Inferência de Status Atual
    const lastMove = timeline[0] || {};
    let inferredStatus = 'Ativo';
    if (['ARQUIVADO', 'BAIXA'].includes(lastMove.type)) inferredStatus = 'Arquivado';
    if (lastMove.type === 'SUSPENSAO') inferredStatus = 'Suspenso';
    if (lastMove.type === 'SENTENCA') inferredStatus = 'Sentença';

    return {
        ...basicInfo,
        timeline,
        statusSugerido: inferredStatus
    };
};

/**
 * Simulador de Resposta (Mock para quando não houver API Key)
 */
export const getMockDataJud = (cnj) => {
    return {
        numeroProcesso: cnj,
        classe: { nome: 'Procedimento Comum Cível' },
        assuntos: [{ nome: 'Indenização por Dano Moral' }],
        tribunal: 'TJPR',
        dataAjuizamento: new Date().toISOString(),
        valorCausa: 15000.00,
        movimentos: [
            { codigo: 11383, nome: 'Ato Ordinatório', dataHora: new Date().toISOString(), complemento: 'Intimação de leitura realizada.' },
            { codigo: 85, nome: 'Petição Juntada', dataHora: new Date(Date.now() - 86400000 * 5).toISOString(), complemento: 'Contestação apresentada pelo Réu.' },
            { codigo: 196, nome: 'Audiência Designada', dataHora: new Date(Date.now() - 86400000 * 15).toISOString(), complemento: 'Conciliação via Microsoft Teams.' }
        ]
    };
};